import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PAGES, SITE, ORGANIZATION_SCHEMA, SOFTWARE_SCHEMA, breadcrumbSchema, faqSchema } from './seoData';

/* ═══════════════════════════════════════════════════════════════════
   SeoPageLayout — premium, founder-focused cornerstone page layout.
   Single component renders every cornerstone page from its data.
   ═══════════════════════════════════════════════════════════════════ */

const C = {
  lime: '#c8f135',
  limeHover: '#d4f74a',
  bg: '#04050a',
  surface: 'rgba(255,255,255,0.02)',
  border: 'rgba(255,255,255,0.07)',
  borderLime: 'rgba(200,241,53,0.22)',
  text: '#f0f0e8',
  muted: '#3a4830',
  dim: '#1e2818',
};
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, "Cascadia Mono", Consolas, monospace';
const DISP = "'Syne', system-ui, sans-serif";

const sectionPad = 'clamp(80px, 12vw, 140px) clamp(20px, 5vw, 48px)';

/* ────────── SEO head injection ────────── */
const setMeta = (selector, attr, content) => {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, key, val] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
    if (key && val) el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, content);
};
const setLink = (rel, href) => {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};
const setJsonLd = (id, data) => {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};
const removeJsonLd = (id) => {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id);
  if (el) el.remove();
};

const useSeo = (page) => {
  useEffect(() => {
    if (!page) return;
    const canonical = `${SITE.domain}/${page.slug}`;
    const ogImg = `${SITE.domain}/og/${page.slug}.png`;
    document.title = page.title;
    setMeta('meta[name="description"]', 'content', page.description);
    setMeta('meta[property="og:title"]', 'content', page.title);
    setMeta('meta[property="og:description"]', 'content', page.description);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', ogImg);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', page.title);
    setMeta('meta[name="twitter:description"]', 'content', page.description);
    setMeta('meta[name="twitter:image"]', 'content', ogImg);
    setLink('canonical', canonical);

    setJsonLd('ld-org', ORGANIZATION_SCHEMA);
    setJsonLd('ld-software', SOFTWARE_SCHEMA);
    setJsonLd('ld-breadcrumb', breadcrumbSchema(page));
    setJsonLd('ld-faq', faqSchema(page));

    return () => {
      removeJsonLd('ld-breadcrumb');
      removeJsonLd('ld-faq');
    };
  }, [page]);
};

/* ────────── Global style ────────── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&display=swap');
    .seo-fade-in { opacity: 0; transform: translateY(24px); animation: seoFade 0.8s cubic-bezier(0.16,1,0.3,1) forwards; }
    @keyframes seoFade { to { opacity: 1; transform: translateY(0); } }
    .seo-cta-primary {
      display: inline-flex; align-items: center; gap: 8px;
      background: ${C.lime}; color: ${C.bg};
      padding: 14px 26px; border-radius: 10px;
      font-family: ${MONO}; font-size: 12px; font-weight: 700;
      letter-spacing: 0.04em; text-transform: uppercase;
      box-shadow: 0 0 40px rgba(200,241,53,0.28);
      transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), box-shadow 0.25s, background 0.25s;
      cursor: pointer; border: none;
    }
    .seo-cta-primary:hover { transform: translateY(-3px); box-shadow: 0 0 60px rgba(200,241,53,0.45); background: ${C.limeHover}; }
    .seo-cta-ghost {
      display: inline-flex; align-items: center; gap: 8px;
      background: transparent; color: ${C.text};
      padding: 13px 24px; border-radius: 10px;
      font-family: ${MONO}; font-size: 12px; font-weight: 600;
      letter-spacing: 0.04em; text-transform: uppercase;
      border: 1px solid ${C.border};
      transition: border-color 0.2s, background 0.2s;
      cursor: pointer;
    }
    .seo-cta-ghost:hover { border-color: ${C.borderLime}; background: rgba(200,241,53,0.04); }
    details > summary { list-style: none; cursor: pointer; }
    details > summary::-webkit-details-marker { display: none; }
    .seo-nav-link { font-family: ${MONO}; font-size: 11px; color: ${C.muted}; letter-spacing: 0.04em; text-transform: uppercase; transition: color 0.2s; }
    .seo-nav-link:hover { color: ${C.lime}; }
  `}</style>
);

/* ────────── Nav ────────── */
const Nav = () => {
  const navigate = useNavigate();
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', gap: 32,
      padding: '14px clamp(20px, 4vw, 48px)',
      background: 'rgba(4,5,10,0.92)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      borderBottom: `1px solid ${C.border}`,
    }} data-testid="seo-nav">
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} data-testid="seo-nav-logo">
        <div style={{ width: 28, height: 28, borderRadius: 7, background: C.lime, color: C.bg, display: 'grid', placeItems: 'center', fontFamily: DISP, fontWeight: 800, fontSize: 14 }}>S</div>
        <span style={{ fontFamily: DISP, fontWeight: 800, fontSize: 15, color: C.text, letterSpacing: '-0.01em' }}>ScalitOS</span>
      </Link>
      <div style={{ display: 'flex', gap: 28, marginRight: 'auto' }} className="hidden md:flex">
        <Link to="/marketing-os" className="seo-nav-link">Platform</Link>
        <Link to="/campaign-studio" className="seo-nav-link">Campaigns</Link>
        <Link to="/competitor-analysis" className="seo-nav-link">Intelligence</Link>
        <Link to="/blog" className="seo-nav-link">Blog</Link>
      </div>
      <button onClick={() => navigate('/login')} data-testid="seo-nav-login" className="seo-nav-link" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Sign in</button>
      <button onClick={() => navigate('/register')} data-testid="seo-nav-cta" style={{
        background: C.lime, color: C.bg, padding: '9px 16px', borderRadius: 8,
        fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
        border: 'none', cursor: 'pointer',
      }}>Start free &rarr;</button>
    </nav>
  );
};

/* ────────── Breadcrumb ────────── */
const Breadcrumb = ({ label }) => (
  <nav aria-label="Breadcrumb" style={{ padding: '24px clamp(20px, 5vw, 48px) 0', maxWidth: 1200, margin: '0 auto' }}>
    <ol style={{ display: 'flex', gap: 8, fontFamily: MONO, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.muted, listStyle: 'none', padding: 0 }}>
      <li><Link to="/" style={{ color: C.muted, textDecoration: 'none' }}>Home</Link></li>
      <li aria-hidden="true">/</li>
      <li style={{ color: C.lime }}>{label}</li>
    </ol>
  </nav>
);

/* ────────── Footer ────────── */
const Footer = () => (
  <footer style={{ padding: 'clamp(60px, 8vw, 100px) clamp(20px, 5vw, 48px) 60px', borderTop: `1px solid ${C.border}` }}>
    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 40 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: C.lime, color: C.bg, display: 'grid', placeItems: 'center', fontFamily: DISP, fontWeight: 800, fontSize: 14 }}>S</div>
          <span style={{ fontFamily: DISP, fontWeight: 800, fontSize: 16, color: C.text, letterSpacing: '-0.01em' }}>ScalitOS</span>
        </div>
        <p style={{ fontFamily: MONO, fontSize: 11, color: C.muted, lineHeight: 1.8, maxWidth: 240 }}>
          The marketing operating system for solo founders and lean teams.
        </p>
      </div>
      {[
        { h: 'Platform', l: [['/marketing-os', 'Marketing OS'], ['/campaign-studio', 'Campaign Studio'], ['/ad-creative-generator', 'Ad Creative'], ['/landing-page-generator', 'Landing Pages'], ['/email-campaigns', 'Email'], ['/competitor-analysis', 'Competitor Intel']] },
        { h: 'Company', l: [['/blog', 'Blog'], ['/pitch', 'Pitch deck'], ['/legal/privacy', 'Privacy'], ['/legal/terms', 'Terms']] },
        { h: 'Get started', l: [['/register', 'Create account'], ['/login', 'Sign in']] },
      ].map((col) => (
        <div key={col.h}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 14 }}>{col.h}</p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {col.l.map(([href, label]) => (
              <li key={href}><Link to={href} style={{ fontFamily: MONO, fontSize: 11, color: C.text, textDecoration: 'none', letterSpacing: '0.02em' }}>{label}</Link></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <p style={{ marginTop: 60, fontFamily: MONO, fontSize: 10, color: C.muted, textAlign: 'center', letterSpacing: '0.04em' }}>
      &copy; {new Date().getFullYear()} ScalitOS &middot; Built for founders who are tired of glueing tools together.
    </p>
  </footer>
);

/* ────────── Page ────────── */
const SeoPageLayout = ({ slug }) => {
  const page = PAGES[slug];
  const navigate = useNavigate();
  useSeo(page);

  if (!page) {
    return (
      <div style={{ background: C.bg, color: C.text, minHeight: '100vh', padding: 60 }}>
        <h1>Page not found</h1>
        <Link to="/" style={{ color: C.lime }}>Back to home</Link>
      </div>
    );
  }

  return (
    <div data-testid={`seo-page-${slug}`} style={{ background: C.bg, color: C.text, fontFamily: MONO, minHeight: '100vh' }}>
      <GlobalStyle />
      <Nav />
      <Breadcrumb label={page.breadcrumb} />

      {/* HERO */}
      <header style={{ padding: 'clamp(60px, 9vw, 110px) clamp(20px, 5vw, 48px) clamp(40px, 6vw, 80px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p className="seo-fade-in" style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 18 }}>{page.kicker}</p>
          <h1 className="seo-fade-in" style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(40px, 7.2vw, 84px)', letterSpacing: '-0.04em', lineHeight: 0.98, color: C.text, animationDelay: '120ms' }}>
            <span style={{ display: 'block' }}>{page.h1Top}</span>
            <span style={{ display: 'block', color: C.lime }}>{page.h1Bottom}</span>
          </h1>
          <p className="seo-fade-in" style={{ marginTop: 28, fontFamily: MONO, fontSize: 14, lineHeight: 1.9, color: C.muted, maxWidth: 640, animationDelay: '240ms' }}>
            {page.subhead}
          </p>
          <div className="seo-fade-in" style={{ marginTop: 36, display: 'flex', flexWrap: 'wrap', gap: 12, animationDelay: '360ms' }}>
            <button onClick={() => navigate('/register')} className="seo-cta-primary" data-testid={`hero-cta-${slug}`}>Start free &rarr;</button>
            <Link to="/marketing-os" className="seo-cta-ghost" style={{ textDecoration: 'none' }}>See the platform</Link>
          </div>
          <p className="seo-fade-in" style={{ marginTop: 16, fontFamily: MONO, fontSize: 9, letterSpacing: '0.22em', color: C.muted, textTransform: 'uppercase', animationDelay: '480ms' }}>
            Free forever &middot; No credit card &middot; Ready in 3 minutes
          </p>
        </div>
      </header>

      {/* PROBLEM */}
      <section style={{ padding: sectionPad, background: 'rgba(255,255,255,0.012)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>The problem</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 52px)', lineHeight: 1.05, letterSpacing: '-0.025em', color: C.text }}>
            {page.problem.heading}
          </h2>
          <p style={{ marginTop: 28, fontFamily: MONO, fontSize: 14, lineHeight: 1.9, color: 'rgba(240,240,232,0.7)', maxWidth: 760 }}>
            {page.problem.body}
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>How it works</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 52px)', lineHeight: 1.05, letterSpacing: '-0.025em', color: C.text, marginBottom: 48 }}>
            Built for shipping, not for dashboards.
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 18 }}>
            {page.pillars.map((p) => (
              <article key={p.k} style={{ padding: '28px 24px', border: `1px solid ${C.border}`, borderRadius: 16, background: C.surface }}>
                <p style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: C.lime, letterSpacing: '0.12em', marginBottom: 14 }}>{p.k}</p>
                <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 20, lineHeight: 1.2, color: C.text, marginBottom: 12, letterSpacing: '-0.01em' }}>{p.title}</h3>
                <p style={{ fontFamily: MONO, fontSize: 12, lineHeight: 1.8, color: 'rgba(240,240,232,0.55)' }}>{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section style={{ padding: sectionPad, background: 'rgba(255,255,255,0.012)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>The workflow</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 52px)', textAlign: 'center', lineHeight: 1.05, letterSpacing: '-0.025em', color: C.text, marginBottom: 56 }}>
            Four steps. One workspace.
          </h2>
          <ol style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 28 }}>
            {page.workflow.map((w) => (
              <li key={w.s} style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 22, padding: '24px 0', borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontFamily: DISP, fontWeight: 800, fontSize: 32, color: C.lime, letterSpacing: '-0.02em' }}>{w.s}</span>
                <div>
                  <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 22, lineHeight: 1.2, color: C.text, marginBottom: 8, letterSpacing: '-0.015em' }}>{w.title}</h3>
                  <p style={{ fontFamily: MONO, fontSize: 13, lineHeight: 1.9, color: 'rgba(240,240,232,0.6)' }}>{w.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(60px, 12vw, 140px)', color: C.lime, letterSpacing: '-0.04em', lineHeight: 0.95 }}>{page.socialProof.stat}</p>
          <p style={{ marginTop: 18, fontFamily: MONO, fontSize: 14, lineHeight: 1.7, color: C.muted, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto' }}>
            {page.socialProof.label}
          </p>
        </div>
      </section>

      {/* MID CTA */}
      <section style={{ padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 48px)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 36px', border: `1px solid ${C.borderLime}`, borderRadius: 20, background: 'linear-gradient(135deg, rgba(200,241,53,0.06), rgba(200,241,53,0.01))', textAlign: 'center' }}>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4.4vw, 44px)', letterSpacing: '-0.025em', lineHeight: 1.1, color: C.text, marginBottom: 14 }}>
            Stop renting a marketing team. Run your own.
          </h2>
          <p style={{ fontFamily: MONO, fontSize: 13, color: C.muted, marginBottom: 28, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.8 }}>
            ScalitOS is free to start, no card required. Most founders see results inside their first week.
          </p>
          <button onClick={() => navigate('/register')} className="seo-cta-primary" data-testid={`mid-cta-${slug}`}>Get your marketing OS &rarr;</button>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: sectionPad, background: 'rgba(255,255,255,0.012)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>Frequently asked</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 52px)', lineHeight: 1.05, letterSpacing: '-0.025em', color: C.text, marginBottom: 36 }}>
            Questions founders actually ask.
          </h2>
          <div>
            {page.faqs.map((f, i) => (
              <details key={i} style={{ borderTop: `1px solid ${C.border}`, padding: '22px 0' }}>
                <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                  <h3 style={{ fontFamily: DISP, fontWeight: 700, fontSize: 18, color: C.text, letterSpacing: '-0.01em' }}>{f.q}</h3>
                  <span style={{ fontFamily: DISP, fontWeight: 700, fontSize: 22, color: C.lime, lineHeight: 1 }}>+</span>
                </summary>
                <p style={{ marginTop: 14, fontFamily: MONO, fontSize: 13, lineHeight: 1.85, color: 'rgba(240,240,232,0.65)', maxWidth: 760 }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: C.lime, textTransform: 'uppercase', marginBottom: 14 }}>Keep exploring</p>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(24px, 4vw, 40px)', lineHeight: 1.1, letterSpacing: '-0.025em', color: C.text, marginBottom: 32 }}>
            Adjacent surfaces of the platform.
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
            {page.related.map(([href, label]) => (
              <Link key={href} to={href} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 22px', border: `1px solid ${C.border}`, borderRadius: 14, background: C.surface, textDecoration: 'none', transition: 'border-color 0.2s, transform 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderLime; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <span style={{ fontFamily: DISP, fontWeight: 700, fontSize: 16, color: C.text, letterSpacing: '-0.01em' }}>{label}</span>
                <span style={{ color: C.lime, fontFamily: MONO, fontSize: 14 }}>&rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ padding: sectionPad, textAlign: 'center', borderTop: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(32px, 6vw, 64px)', letterSpacing: '-0.035em', lineHeight: 1.05, color: C.text, marginBottom: 22 }}>
            Run your marketing like a real operator.
          </h2>
          <p style={{ fontFamily: MONO, fontSize: 13, lineHeight: 1.9, color: C.muted, marginBottom: 32 }}>
            ScalitOS is the operating system founders use to plan, ship, and measure every campaign — without the agency, without the chaos.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => navigate('/register')} className="seo-cta-primary" data-testid={`final-cta-${slug}`}>Start free &rarr;</button>
            <Link to="/blog" className="seo-cta-ghost" style={{ textDecoration: 'none' }}>Read the blog</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeoPageLayout;
